import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { InputState } from '../InputState';
import { addRejection } from '../../../../../services/api/orders.endpoints';

const REJECTION_REASONS = ['En falta', 'Receta vencida', 'No trabajamos ese plan', 'otros'];

interface RejectOrderButtonProps {
    orderId: string;
    inputState: InputState;
}

function RejectOrderButton({ orderId, inputState }: RejectOrderButtonProps) {
    const navigate = useNavigate();
    const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleRejectButton = () => {
        setRejectionDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setRejectionDialogOpen(false);
        setSelectedReason('');
        setCustomReason('');
    };

    const handleConfirmRejection = async () => {
        if (!selectedReason) return;

        setIsSubmitting(true);
        try {
            const reason = selectedReason === 'otros' ? customReason : selectedReason;
            await addRejection(orderId, reason).then(() => navigate('/'));
            handleCloseDialog();
            // The order will disappear from the page due to shouldBeShown logic
        } catch (error) {
            console.error('Error rejecting order:', error);
            // TODO: Show error message to user
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* REJECT BUTTON */}
            <Button
                onClick={handleRejectButton}
                size="medium"
                variant="outlined"
                color="warning"
                disabled={inputState === 'disabled'}
                sx={{ minWidth: 120 }}>
                Rechazar
            </Button>

            {/* REJECTION DIALOG */}
            <Dialog open={rejectionDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Rechazar Pedido</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Al rechazar este pedido, desaparecerá de la ventana y será mostrado a otra
                        farmacia para que pueda atender al paciente.
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Motivo del rechazo</InputLabel>
                        <Select
                            value={selectedReason}
                            label="Motivo del rechazo"
                            onChange={(e) => setSelectedReason(e.target.value)}>
                            {REJECTION_REASONS.map((reason) => (
                                <MenuItem key={reason} value={reason}>
                                    {reason}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedReason === 'otros' && (
                        <TextField
                            fullWidth
                            label="Motivo"
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            placeholder="El paciente no verá este mensaje"
                            multiline
                            rows={2}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmRejection}
                        variant="contained"
                        color="warning"
                        disabled={
                            !selectedReason ||
                            (selectedReason === 'otros' && !customReason.trim()) ||
                            isSubmitting
                        }>
                        {isSubmitting ? 'Rechazando...' : 'Rechazar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export { RejectOrderButton };
